/**
 * The date arithmetic Today's chrome needs, kept pure and away from the screen.
 *
 * The one rule here: the day comes from the server's `TodaySummary.date`, which is already in
 * `profile.timezone`, never from `new Date()` in the browser. A learner in Tbilisi opening the
 * app at 01:00 must see the same day the streak was computed against.
 */

/**
 * `2026-08-19` → "Wednesday, Aug 19".
 *
 * Built and formatted in UTC on purpose: `new Date('2026-08-19')` is UTC midnight, so
 * formatting it in a negative-offset local zone would render the day before.
 */
export function formatTodayDate(isoDate: string, locale?: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** The calendar date an instant fell on, in the learner's zone. `en-CA` is ISO-shaped. */
export function localDateOf(isoDateTime: string, timeZone: string): string {
  return new Date(isoDateTime).toLocaleDateString('en-CA', { timeZone });
}

/** Whole days between two `YYYY-MM-DD` dates, by calendar arithmetic in UTC. */
export function daysBetween(from: string, to: string): number {
  const at = (s: string): number => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((at(to) - at(from)) / 86_400_000);
}

/**
 * "last watched ___". Plain words for the days someone would use words for, and a date after
 * that — "7 days ago" is a number to decode, where "Aug 12" is just when it was.
 */
export function relativeDay(watched: string, today: string, locale?: string): string {
  const ago = daysBetween(watched, today);
  if (ago <= 0) return 'today';
  if (ago === 1) return 'yesterday';
  if (ago < 7) return `${ago} days ago`;
  const [y, m, d] = watched.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The YouTube thumbnail the embed itself would show. `i.ytimg.com` is the player's own host —
 * this is not hosting third-party video, it is the same still the IFrame paints. Uploads get no
 * still: a per-timestamp frame is not achievable for an embed and is not attempted.
 */
export function youtubeStill(provider: string, externalId: string): string | null {
  return provider === 'youtube'
    ? `https://i.ytimg.com/vi/${encodeURIComponent(externalId)}/hqdefault.jpg`
    : null;
}
